// Where to send the buyer after Stripe.
//
// A Stripe Payment Link allows exactly ONE static "after payment" URL, but the
// page worth returning to is per-deck (/host/deck/{deckId}) — so Stripe alone
// can never land anyone back where they actually were. The controller stashes
// its path here on the way out; /upgrade/success reads it and bounces. That
// works across tabs because checkout opens in a NEW tab and localStorage is
// per-origin: the tab Stripe redirects reads what the controller tab wrote.

const RETURN_KEY = "copresentUpgradeReturnTo";

// Query flag the bounce appends, and the controller reacts to by showing the
// payment-received dialog.
export const UPGRADED_PARAM = "upgraded";

// Only ever a same-origin PATH.
//
// This value is read back out of storage and navigated to, so it is untrusted
// input by the time we use it. "//evil.com" is a protocol-relative URL that
// would leave the site entirely, and it starts with "/" — hence the second
// check rather than just the first.
const isSafePath = (v: string | null): v is string =>
  typeof v === "string" &&
  v.startsWith("/") &&
  !v.startsWith("//") &&
  !v.startsWith("/\\");

export const rememberUpgradeReturn = (path: string): void => {
  if (!isSafePath(path)) return;
  try {
    window.localStorage.setItem(RETURN_KEY, path);
  } catch {
    // Private mode / storage disabled. The receipt page is the fallback, so
    // losing the return path costs polish, not the purchase.
  }
};

// Memoised for the life of the page load. The read below CLEARS storage, and
// React StrictMode invokes state initializers twice in development — without
// this, the second call would get null back and silently disable the bounce in
// dev only. Callers get a stable answer; storage is still cleared exactly once.
let taken: string | null | undefined;

// Read-and-clear: a stored return is good for exactly ONE trip. Leaving it
// behind would bounce someone who later opens /upgrade/success directly into a
// deck they may no longer be presenting.
export const takeUpgradeReturn = (): string | null => {
  if (taken !== undefined) return taken;
  try {
    const stored = window.localStorage.getItem(RETURN_KEY);
    window.localStorage.removeItem(RETURN_KEY);
    taken = isSafePath(stored) ? stored : null;
  } catch {
    taken = null;
  }
  return taken;
};
